import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PROPERTIES_DATA } from '../data/properties';
import PropertyCard from '../components/PropertyCard';

// Animated counter component
const AnimatedCounter = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
    >
      <motion.span
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5 }}
      >
        {isInView ? (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {value}{suffix}
          </motion.span>
        ) : '0'}
      </motion.span>
    </motion.span>
  );
};

// Stagger container variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const HomePage = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  
  const homeProperties = PROPERTIES_DATA.slice(0, 3).map(p => ({
    ...p,
    name: t(p.nameKey),
    location: t(p.locationKey),
    description: t(p.descKey),
  }));
  
  return (
    <div className="bg-background-dark text-white font-display overflow-x-hidden">
      {/* Hero Section with Parallax */}
      <main ref={heroRef} className="relative flex items-center justify-center min-h-[100vh] overflow-hidden">
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y: heroY, scale: heroScale }}
        >
          <div 
            className="w-full h-full bg-cover bg-center" 
            style={{
              backgroundImage: `url("https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=80")`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/50 to-black/40" />
        </motion.div>
        
        <motion.div 
          className="relative z-10 w-full max-w-7xl px-4 flex flex-col items-center gap-8 mt-20"
          style={{ opacity: heroOpacity }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center space-y-6 max-w-4xl mx-auto"
          >
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md"
            >
              <span className="size-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-semibold text-white uppercase tracking-wider">{t('home.welcomeBadge')}</span>
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[1.1] tracking-tight"
            >
              {t('home.heroTitle1')} <br />
              <motion.span 
                className="text-primary inline-block"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                {t('home.heroTitle2')}
              </motion.span>
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto font-light leading-relaxed"
            >
              {t('home.heroSubtitle')}
            </motion.p>
            
            <motion.div variants={itemVariants} className="pt-4">
              <Link to="/properties">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(212,175,53,0.5)" }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 bg-primary hover:bg-primary-hover text-surface-dark font-bold text-lg rounded-full px-8 py-4 transition-colors shadow-[0_0_20px_rgba(212,175,53,0.4)]"
                >
                  <span>{t('common.browseProperties')}</span>
                  <motion.span 
                    className="material-symbols-outlined"
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                  >
                    arrow_forward
                  </motion.span>
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </main>

      {/* About Section */}
      <section className="py-24 lg:py-32 bg-surface-dark relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
            <motion.div 
              className="w-full lg:w-1/2 order-2 lg:order-1"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative">
                {/* Decorative frame */}
                <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-primary/30 rounded-tl-2xl" />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-primary/30 rounded-br-2xl" />
                
                <motion.div 
                  className="relative rounded-2xl overflow-hidden shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                >
                  <div 
                    className="aspect-[4/3] w-full bg-cover bg-center" 
                    style={{
                      backgroundImage: `url("https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&q=80")`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div 
              className="w-full lg:w-1/2 order-1 lg:order-2 space-y-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div>
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="text-xs font-bold text-primary uppercase tracking-widest">{t('home.aboutHostBadge')}</span>
                </motion.div>
                <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight">
                  {t('home.aboutTitle1')} <br />
                  <span className="text-primary">{t('home.aboutTitle2')}</span>
                </h2>
              </div>
              
              <div className="space-y-6 text-white/70 text-lg font-light leading-relaxed">
                <p>{t('home.aboutText1')}</p>
                <p>{t('home.aboutText2')}</p>
              </div>
              
              <motion.div 
                className="grid grid-cols-2 gap-8 pt-6 border-t border-white/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div>
                  <h4 className="text-4xl font-bold text-white mb-1">
                    <AnimatedCounter value="500" suffix="+" />
                  </h4>
                  <p className="text-sm text-white/50 uppercase tracking-wider font-medium">{t('home.happyGuests')}</p>
                </div>
                <div>
                  <h4 className="text-4xl font-bold text-white mb-1">
                    <AnimatedCounter value="4.9" />
                  </h4>
                  <p className="text-sm text-white/50 uppercase tracking-wider font-medium">{t('home.avgRating')}</p>
                </div>
              </motion.div>
              
              <motion.div 
                className="pt-4"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <Link to="/about" className="group inline-flex items-center gap-3 text-surface-dark bg-primary hover:bg-primary-hover px-8 py-4 rounded-full font-bold transition-all shadow-lg hover:shadow-[0_0_30px_rgba(212,175,53,0.4)]">
                  <span>{t('common.learnMore')}</span>
                  <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Properties Section */}
      <section className="py-20 lg:py-28 bg-surface-dark border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t('home.featuredTitle')}</h2>
              <p className="text-white/60 max-w-xl text-lg">{t('home.featuredSubtitle')}</p>
            </div>
            <Link to="/properties" className="hidden md:inline-flex items-center gap-2 text-primary font-bold hover:text-primary-hover transition-colors group">
              <span>{t('common.viewAll')}</span>
              <motion.span 
                className="material-symbols-outlined"
                whileHover={{ x: 5 }}
              >
                arrow_forward
              </motion.span>
            </Link>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {homeProperties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.15,
                  ease: [0.22, 1, 0.36, 1]
                }}
              >
                <PropertyCard property={property} />
              </motion.div>
            ))}
          </div>
          
          {/* Mobile view all button */}
          <motion.div 
            className="mt-10 text-center md:hidden"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/properties" className="inline-flex items-center gap-2 bg-primary text-surface-dark font-bold px-6 py-3 rounded-full">
              <span>{t('common.viewAll')}</span>
              <span className="material-symbols-outlined">arrow_forward</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Get in Touch Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-background-dark">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        
        <motion.div 
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="space-y-8">
            <div className="space-y-4">
              <motion.h2 
                className="text-4xl lg:text-6xl font-black text-white tracking-tight leading-none"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                {t('home.contactTitle')} <span className="text-primary">{t('home.contactTitleHighlight')}</span>
              </motion.h2>
              <motion.p 
                className="text-lg text-white/60 font-light max-w-2xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                {t('home.contactSubtitle')}
              </motion.p>
            </div>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <motion.a 
                href="tel:+385911234567" 
                className="flex items-center justify-center gap-4 bg-white/5 border border-white/10 px-8 py-5 rounded-xl backdrop-blur-sm transition-all group"
                whileHover={{ scale: 1.02, borderColor: "rgba(212,175,53,0.5)", backgroundColor: "rgba(255,255,255,0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="size-12 rounded-full bg-primary/20 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-black transition-all duration-300">
                  <span className="material-symbols-outlined text-2xl">call</span>
                </div>
                <div className="text-left">
                  <p className="text-xs text-white/40 uppercase font-bold tracking-wider">{t('common.callOrWhatsApp')}</p>
                  <p className="text-white font-bold text-lg">{t('common.phone')}</p>
                </div>
              </motion.a>
              
              <motion.a 
                href="mailto:info@sunsetapartments.com" 
                className="flex items-center justify-center gap-4 bg-white/5 border border-white/10 px-8 py-5 rounded-xl backdrop-blur-sm transition-all group"
                whileHover={{ scale: 1.02, borderColor: "rgba(212,175,53,0.5)", backgroundColor: "rgba(255,255,255,0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="size-12 rounded-full bg-primary/20 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-black transition-all duration-300">
                  <span className="material-symbols-outlined text-2xl">mail</span>
                </div>
                <div className="text-left">
                  <p className="text-xs text-white/40 uppercase font-bold tracking-wider">{t('common.emailMe')}</p>
                  <p className="text-white font-bold text-lg">{t('common.email')}</p>
                </div>
              </motion.a>
            </motion.div>

            <motion.div 
              className="pt-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(212,175,53,0.5)" }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-3 bg-primary hover:bg-primary-hover text-surface-dark font-bold text-lg rounded-full px-8 py-4 transition-colors shadow-[0_0_20px_rgba(212,175,53,0.3)]"
                >
                  <span>{t('common.sendMessage')}</span>
                  <span className="material-symbols-outlined">arrow_forward</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
